<template>
  <div class="reliability" v-if="!isLoading && hasData">
    <reliability-table
      :rows="formattedTransitIndicators"
      :week-interval="weekInterval"
      @cell-change="onTransitCellChange"
      table-mode="transit"/>
    <reliability-table
      :rows="formattedStockIndicators"
      :week-interval="weekInterval"
      @cell-change="onStockCellChange"
      table-mode="stock"/>
    <table>
      <tbody class="table-body">
      <tr v-for="(row, key, i) in totalIndicators">
        <td class="left bold">{{ $t(`reliability.sumTableRows.${key}`) }}</td>
        <td v-for="cell in row.details">{{ cell.value }}</td>
        <td class="bold">{{ row.value }}</td>
      </tr>
      <tr>
        <td class="left bold">{{ $t(`reliability.sumTableRows.errorsRate`) }}</td>
        <td v-for="cell in errorsRateIndicators.details">{{ `${cell.value.toFixed(2)}%` }}</td>
        <td class="bold"> {{ `${errorsRateIndicators.value.toFixed(2)}%` }}</td>
      </tr>
      </tbody>
    </table>

    <floating-button @validate="sendDatas()" class="floating-validate" color="green" icon="accept" text="Valider"/>
  </div>
  <loader color="#DA4453" v-else-if="isLoading"/>
  <no-data v-else/>
</template>

<script>
  import ReliabilityTable from '@/components/reliability/ReliabilityTable'
  import indicatorsService from '@/services/indicatorsService'
  import { debounced } from '@/utils/decorators'
  import { sendErrorToast } from '@/utils/utils'
  import { DateTime, Interval } from 'luxon'
  import { mapGetters } from 'vuex'

  const TOTAL_TABLE_ROWS_HEADER = ['preparedParcels', 'controledParcels', 'totalErrors']

  export default {
    name: 'Reliability',
    data() {
      return {
        transitIndicators: null,
        stockIndicators: null,
        isLoading: false
      }
    },
    components: { ReliabilityTable },

    created() {
      this.fetchData()
    },

    watch: {
      'site'(newValue, oldValue) {
        if (this.isValidContext) this.fetchData()
      },

      'sector'(newValue, oldValue) {
        if (this.isValidContext && this.site.sectors.length > 1) this.fetchData()
      },

      'period'(newValue, oldValue) {
        if (this.isValidContext) this.fetchData()
      }
    },

    computed: {
      ...mapGetters('context', ['site', 'sector', 'period', 'isValidContext']),

      hasData() {
        return this.transitIndicators && this.stockIndicators
      },

      weekInterval() {
        const week = DateTime.fromISO(this.period).toISOWeekDate()
        const interval = Interval.fromDateTimes(DateTime.fromISO(week).startOf('week'), DateTime.fromISO(week).endOf('week'))
        return interval.splitBy({ days: 1 }).map(d => d.start)
      },

      totalIndicators() {
        let stock = this.formattedStockIndicators
        let transit = this.formattedTransitIndicators

        return TOTAL_TABLE_ROWS_HEADER.reduce((acc, key) => {
          const details = stock[key].details.map((stockCell, i) => {
            return { value: stockCell.value + transit[key].details[i].value, date: stockCell.date }
          })

          acc[key] = {
            value: stock[key].value + transit[key].value,
            details
          }

          return acc
        }, {})
      },

      errorsRateIndicators() {
        let totalControledParcels = this.totalIndicators.controledParcels
        let totalErrors = this.totalIndicators.totalErrors

        const details = totalControledParcels.details.map((controledParcelcell, i) => {
          return {
            value: totalErrors.details[i].value > 0 && controledParcelcell.value > 0 ? (totalErrors.details[i].value / controledParcelcell.value) * 100 : 0,
            date: controledParcelcell.date
          }
        })

        return {
          details,
          value: totalControledParcels.value > 0 && totalErrors.value > 0 ? (totalErrors.value / totalControledParcels.value) * 100 : 0
        }
      },

      formattedTransitIndicators() {
        return this.formatIndicators(this.transitIndicators)
      },

      formattedStockIndicators() {
        return this.formatIndicators(this.stockIndicators)
      }
    },

    methods: {
      formatIndicators(indicators) {
        let rowsWithSum = this.getRowsSum(indicators)
        let buildTotalRow = this.getTotalRow(indicators)

        rowsWithSum.totalErrors = {
          details: buildTotalRow,
          value: buildTotalRow.reduce((acc, cell) => acc + (cell.value ? cell.value : 0), 0)
        }

        return rowsWithSum
      },

      getRowsSum(rows) {
        return Object.keys(rows).reduce((acc, key, i) => {
          acc[key] = {
            value: rows[key].reduce((acc, cell) => acc + (cell.value ? cell.value : 0), 0),
            details: rows[key]
          }
          return acc
        }, {})
      },

      getTotalRow(rows) {
        return this.weekInterval.map((d, i) => {
          return {
            date: d.toLocaleString(),
            value: Object.keys(rows).reduce((acc, key) => {
              if (key === 'preparedParcels' || key === 'controledParcels' || key === 'reversal') return acc
              return acc + (rows[key][i].value ? rows[key][i].value : 0)
            }, 0)
          }
        })
      },

      onTransitCellChange({ key, cell, value }) {
        const initialValue = this.transitIndicators[key][cell].value
        const errorsSupControled = key === 'controledParcels' && value > this.transitIndicators.preparedParcels[cell].value
        const controledSupPrepared = key !== 'preparedParcels' && key !== 'controledParcels' && value > this.transitIndicators.controledParcels[cell].value

        //Force Dom Change by assigning the newValue even if there's an error to reload input value and then check error.
        this.transitIndicators[key][cell].value = value
        if (errorsSupControled || controledSupPrepared || value < 0) {
          this.transitIndicators[key][cell].value = initialValue
          sendErrorToast(this.$t(errorsSupControled ? 'reliability.errors.errorsSupControled' :
                                 controledSupPrepared ? 'reliability.errors.errorsSupControled' : 'reliability.errors.valIsInfZero'))
        }
        if (key === 'reversal') this.transitIndicators.countsDouble[cell].value = errorsSupControled || controledSupPrepared ? initialValue * 2 : value * 2
        this.transitIndicators.preparedParcels[cell].updated = true
      },

      onStockCellChange({ key, cell, value }) {
        const initialValue = this.stockIndicators[key][cell].value
        const errorsSupControled = key === 'controledParcels' && value > this.stockIndicators.preparedParcels[cell].value
        const controledSupPrepared = key !== 'preparedParcels' && key !== 'controledParcels' && value > this.stockIndicators.controledParcels[cell].value

        //Force Dom Change by assigning the newValue even if there's an error to reload input value and then check error.
        this.stockIndicators[key][cell].value = value

        if (errorsSupControled || controledSupPrepared || value < 0) {
          this.stockIndicators[key][cell].value = initialValue
          sendErrorToast(this.$t(errorsSupControled ? 'reliability.errors.errorsSupControled' :
                                 controledSupPrepared ? 'reliability.errors.errorsSupControled' : 'reliability.errors.valIsInfZero'))
        }

        if (key === 'reversal') this.stockIndicators.countsDouble[cell].value = errorsSupControled || controledSupPrepared ? initialValue * 2 : value * 2
        this.stockIndicators.preparedParcels[cell].updated = true
      },

      @debounced(1000)
      async sendDatas() {
        this.isLoading = true
        const indicators = { transit: this.transitIndicators, stock: this.stockIndicators }
        await indicatorsService.setReliabilityIndicators({
          site: this.site,
          sector: this.sector,
          startDate: this.weekInterval[0].toFormat('yyyy-MM-dd'),
          indicators
        })
        this.isLoading = false
      },

      async fetchData() {
        this.isLoading = true
        const indicators = await indicatorsService.getReliabilityIndicators({
          site: this.site,
          sector: this.sector,
          startDate: this.weekInterval[0].toFormat('yyyy-MM-dd')
        })
        this.transitIndicators = indicators ? indicators.transit : []
        this.stockIndicators = indicators ? indicators.stock : []
        this.isLoading = false
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "@/components/reliability/ReliabilityTable";

  .reliability, .v-loader, .noData {
    padding: 1.5rem 4rem 0 4rem;
    height: calc(82% - 40px);
    overflow-y: auto;
    background-color: $app-background;
  }

  .floating-undo {
    position: absolute;
    bottom: 90px;
    right: 15px;
  }

  .floating-validate {
    position: absolute;
    bottom: 15px;
    right: 15px;
    background-color: green;
  }

  .reliability::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.1);
    background-color: #F5F5F5;
    border-radius: 10px;
  }

  .reliability::-webkit-scrollbar {
    width: 10px;
    background-color: #F5F5F5;
  }

  .reliability::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: #C9C4C4;
  }
</style>
